'use client';

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from '@/app/[lang]/(main)/components/shared/PrivateLink/ServerLink';
import { useGeoSearchString } from '@/contexts/CookieContext';
import { useGetCategoryIdDict } from '@/hooks/useGetCategoryIdDict';
import {
  CommonParams,
  convertParamsToPath,
  addFreeSegment,
  addPathFragment,
  isPureQueryUrl,
  ParameterValue,
} from '@/utils/SEOPathModule/convertParamsToPath';
import { Dicts } from '@/utils/SEOPathModule/types';

type CategoryLinkProps = {
  categoryLink?: string;
  categoryId?: string;
  commonParams?: CommonParams;
  className?: string;
  dataCategoryLink?: string;
  initialHref: string;
  children: ReactNode;
  title?: string;
  dir?: string;
};

export const buildCategoryIdPath = (categoryId: string, categoryIdDict: Dicts['categoryIdDict']) => {
  const ids: string[] = [];
  const visited = new Set<string>();
  let currentId: string | null | undefined = categoryId;

  while (currentId && !visited.has(currentId)) {
    const category = categoryIdDict[currentId];
    if (!category) {
      break;
    }

    ids.unshift(category.id);
    visited.add(currentId);
    currentId = category.parentId;
  }

  return ids;
};

const normalizeSegment = (value?: string | null) => {
  if (!value) {
    return '';
  }

  return value.replace(/^\/+/, '').replace(/\/+$/, '');
};

export const buildGeoPrefixedPath = (searchHref: string | undefined | null, categoryLink?: string) => {
  const normalizedCategory = normalizeSegment(categoryLink);
  const normalizedGeo = normalizeSegment(searchHref);

  if (!normalizedCategory) {
    return normalizedGeo ? `/${normalizedGeo}` : '/';
  }

  return normalizedGeo ? `/${normalizedGeo}/${normalizedCategory}` : `/${normalizedCategory}`;
};

const appendCommonParams = (href: string, commonParams: CommonParams = {}) => {
  const url = new URL(href || '/', 'http://example.com');

  for (const key in commonParams) {
    const typedKey = key as keyof CommonParams;
    const val = commonParams[typedKey];

    if (typedKey === 'isFree') {
      continue;
    }

    if ((typedKey === 'minPrice' || typedKey === 'maxPrice') && commonParams.isFree === true) {
      continue;
    }

    if (val !== undefined && val !== null) {
      url.searchParams.set(typedKey, String(val));
    }
  }

  let result = url.pathname + url.search + url.hash;

  if (commonParams.isFree === true) {
    result = addFreeSegment(result);
  } else if (isPureQueryUrl(result) && url.search) {
    result = addPathFragment(result, '/search');
  }

  return result;
};

type BuildGeoCategoryHrefInput = {
  searchHref?: string | null;
  categoryLink?: string;
  categoryId?: string;
  categoriesPath?: string[];
  commonParams?: CommonParams;
  parameters?: ParameterValue[];
  categoryIdDict?: Dicts['categoryIdDict'];
};

export const buildGeoCategoryHref = ({
  searchHref,
  categoryLink,
  categoryId,
  categoriesPath = [],
  commonParams,
  parameters,
  categoryIdDict = {},
}: BuildGeoCategoryHrefInput) => {
  if (categoryId && categoryIdDict[categoryId]) {
    try {
      return convertParamsToPath(
        {
          categories: categoriesPath,
          parameters: parameters || [],
          commonParams: commonParams || {},
        },
        {
          categoryIdDict,
        },
        searchHref || undefined,
      );
    } catch {
      // invalid parameter value / dynamic parameter without parent — fall back to a plain category link
    }
  }

  const baseHref = buildGeoPrefixedPath(searchHref, categoryLink);
  return appendCommonParams(baseHref, commonParams);
};

export const useGeoCategoryHref = ({
  categoryLink,
  categoryId,
  commonParams,
  parameters,
}: Pick<CategoryLinkProps, 'categoryLink' | 'categoryId' | 'commonParams'> & {
  parameters?: ParameterValue[];
}) => {
  const searchHref = useGeoSearchString();
  const { data: categoryIdDict } = useGetCategoryIdDict();

  return useMemo(
    () =>
      buildGeoCategoryHref({
        categoriesPath: categoryId ? buildCategoryIdPath(categoryId, categoryIdDict) : [],
        searchHref,
        categoryLink,
        categoryId,
        commonParams,
        parameters,
        categoryIdDict,
      }),
    [searchHref, categoryLink, categoryId, commonParams, parameters, categoryIdDict],
  );
};

export const GeoAwareCategoryLink = ({
  categoryLink,
  categoryId,
  commonParams,
  className,
  dataCategoryLink,
  initialHref,
  children,
  title,
  dir,
}: CategoryLinkProps) => {
  const geoHref = useGeoCategoryHref({ categoryLink, categoryId, commonParams });
  const [href, setHref] = useState(() => initialHref || '/');
  useEffect(() => {
    const nextHref = geoHref || initialHref || '/';
    setHref((current) => (current === nextHref ? current : nextHref));
  }, [geoHref, initialHref]);

  return (
    <Link title={title} href={href} className={className} data-category-link={dataCategoryLink} dir={dir}>
      {children}
    </Link>
  );
};
